import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'lodash';
import { Alert, Button, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useSubscriptionManagerContext } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.context';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import {
  CENTS_TO_DOLLARS_CONVERSION_FACTOR,
  LEAD_LIMIT_ADDON_INCREMENT,
} from '../../../SubscriptionManager.constants';
import { eProductType } from '../../../SubscriptionManager.enums';
import CardSelector from '../../CardSelector/CardSelector.component';

export default function ManageLeadLimitAddonModal() {
  const {
    selectedPaymentMethod,
    toggleAddPaymentMethodModal,
    showManageLeadLimitAddonModal,
    toggleManageLeadLimitAddonModal,
    updateLeadLimitAddonPlan,
    updatingPlan,
    products,
  } = useSubscriptionManagerContext();
  const { subscription } = useSubscriptionContext();
  const { activeLeadsLimit } = useACLContext();
  const { t, i18n } = useTranslation('settings/SubscriptionManager');

  // Check if lead limit addon is active first (needed for correct limit calculation)
  const leadLimitAddonProduct = products?.allRelatedProducts?.find(
    (product) => product.productType === eProductType.LEAD_LIMIT_ADDON,
  );

  const isLeadLimitAddonActive =
    products?.purchasedProducts.some(
      (product) => product.uuid === leadLimitAddonProduct?.uuid,
    ) ?? false;

  // Calculate limits based on addon status
  // If addon is NOT active: activeLeadsLimit is the base limit
  // If addon IS active: activeLeadsLimit already includes the addon, so subtract to get base
  const baseLimit = isLeadLimitAddonActive
    ? Number(activeLeadsLimit) - LEAD_LIMIT_ADDON_INCREMENT
    : Number(activeLeadsLimit);

  // Adjusted limit to show: purchasing shows +15000, canceling shows -15000
  const adjustedLimit = isLeadLimitAddonActive
    ? Number(activeLeadsLimit) - LEAD_LIMIT_ADDON_INCREMENT // Canceling: current - 15000
    : Number(activeLeadsLimit) + LEAD_LIMIT_ADDON_INCREMENT; // Purchasing: current + 15000

  const longDateConfig: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const conversionFormattedDate = subscription.endDate.toLocaleString(
    i18n.language,
    longDateConfig,
  );

  // Using a fixed cost for Lead Limit Addon based on existing code
  const leadLimitAddonCost = leadLimitAddonProduct?.price || 5000;

  return (
    <Modal
      open={showManageLeadLimitAddonModal}
      backdrop
      onClose={() => {
        toggleManageLeadLimitAddonModal(false);
      }}
      className="rounded-lg p-0 min-w-[38.5rem]"
      data-testid="subscription-modal"
    >
      <Modal.Header className="flex justify-between items-center mb-0 p-4 border-b">
        <h1 className="text-lg w-full">
          {t('addOns.manageLeadLimitAddon.title')}
        </h1>
        <XMarkIcon
          onClick={() => {
            toggleManageLeadLimitAddonModal(false);
          }}
          className="size-6"
        />
      </Modal.Header>
      <Modal.Body className="py-6 px-4 form-control gap-4">
        {!isLeadLimitAddonActive ? (
          <div className="flex flex-col gap-1">
            <p>{t('addOns.manageLeadLimitAddon.paymentMethod')}</p>
            {isEmpty(selectedPaymentMethod.paymentMethodId) ? (
              <Button
                className="SubscriptionCheckout__paymentMethods__addPaymentMethodButton"
                onClick={() => toggleAddPaymentMethodModal(true)}
                color="ghost"
                fullWidth
                data-testid="add-payment-method-button"
              >
                {t('page.SubscriptionCheckout.addPaymentMethodButton')}
              </Button>
            ) : (
              <>
                <CardSelector />
                <Button
                  onClick={() => toggleAddPaymentMethodModal(true)}
                  variant="link"
                  size="sm"
                  className="text-neutral-content w-fit px-0"
                >
                  {t('page.SubscriptionCheckout.addPaymentMethod')}
                </Button>
              </>
            )}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {!isLeadLimitAddonActive ? (
            <p className="text-sm text-neutral-500">
              {t('addOns.manageLeadLimitAddon.proportionalCharge')}
              <br />
              {t('addOns.manageLeadLimitAddon.proportionalChargeDescription', {
                date: conversionFormattedDate,
                cost: leadLimitAddonCost / CENTS_TO_DOLLARS_CONVERSION_FACTOR,
                currency: subscription.currency,
                frequency: t(
                  `subscriptionOverview.frequency.${subscription.frequency}`,
                ),
              })}
            </p>
          ) : (
            <>
              <p>
                {t('addOns.manageLeadLimitAddon.deactivationWarning', {
                  totalLimit: Number(activeLeadsLimit).toLocaleString(),
                  baseLimit: adjustedLimit.toLocaleString(),
                })}
              </p>
              <Alert
                icon={<InformationCircleIcon className="size-6 text-primary" />}
                className="border-primary p-2"
              >
                <span className="text-sm">
                  <Trans
                    i18nKey="settings/SubscriptionManager:addOns.manageLeadLimitAddon.deactivationAlert"
                    values={{
                      date: conversionFormattedDate,
                      baseLimit: baseLimit.toLocaleString(),
                    }}
                    components={{
                      strong: <strong />,
                    }}
                  />
                </span>
              </Alert>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions className="p-4 bg-base-300 items-center mt-0" data-testid="modal-actions">
        {!isLeadLimitAddonActive ? (
          <>
            <p className="font-bold text-xl">
              {t('addOns.manageLeadLimitAddon.total', {
                cost: leadLimitAddonCost / CENTS_TO_DOLLARS_CONVERSION_FACTOR,
                currency: subscription.currency,
              })}
            </p>
            <p className="text-xs font-normal flex-1 ">
              {t('addOns.manageLeadLimitAddon.monthlyRecurring', {
                frequency: t(
                  `subscriptionOverview.frequency.${subscription.frequency}`,
                ),
              })}
            </p>
          </>
        ) : null}
        <Button
          color={isLeadLimitAddonActive ? 'error' : 'primary'}
          size="sm"
          className="rounded"
          onClick={() => updateLeadLimitAddonPlan(!isLeadLimitAddonActive)}
          loading={updatingPlan}
          disabled={
            isEmpty(selectedPaymentMethod.paymentMethodId) || updatingPlan
          }
          data-testid="modal-action-button"
        >
          {isLeadLimitAddonActive
            ? t('addOns.manageLeadLimitAddon.confirmDeactivation')
            : t('addOns.manageLeadLimitAddon.buyNow')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
